'use client';
import React, { useState, useCallback, useRef } from 'react';
import { Toaster } from 'sonner';
import type { PlacedComponent, Wire, WireColor, ComponentType } from './circuitTypes';
import ComponentLibrarySidebar from './ComponentLibrarySidebar';
import SimulatorCanvas from './SimulatorCanvas';
import CircuitAnalysisPanel from './CircuitAnalysisPanel';
import SimulatorToolbar from './SimulatorToolbar';
import { validateCircuit } from './circuitValidator';
import type { CircuitResult } from './circuitTypes';

let instanceCounter = 0;

export default function SimulatorClient() {
  const [components, setComponents] = useState<PlacedComponent[]>([]);
  const [wires, setWires] = useState<Wire[]>([]);
  const [selectedColor, setSelectedColor] = useState<WireColor>('live');
  const [circuitResult, setCircuitResult] = useState<CircuitResult | null>(null);
  const [simulationMode, setSimulationMode] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleDropComponent = useCallback((type: ComponentType, x: number, y: number) => {
    instanceCounter += 1;
    const instanceId = `comp-${type}-${instanceCounter}`;
    const labelMap: Record<ComponentType, string> = {
      supply: 'Supply',
      lamp: 'Lamp',
      'switch-one-way': 'SW1',
      'switch-two-way': 'SW2',
      socket: 'Socket',
      'junction-box': 'JB',
      mcb: 'MCB',
      rcbo: 'RCBO',
      fuse: 'Fuse',
      'neutral-bar': 'N-Bar',
      'earth-bar': 'E-Bar',
    };
    setComponents((prev) => [
      ...prev,
      {
        instanceId,
        type,
        x,
        y,
        label: `${labelMap[type]}${instanceCounter}`,
        switchState: type === 'switch-one-way' || type === 'switch-two-way' ? false : undefined,
      },
    ]);
    setCircuitResult(null);
  }, []);

  const handleMoveComponent = useCallback((instanceId: string, x: number, y: number) => {
    setComponents((prev) =>
      prev.map((c) => (c.instanceId === instanceId ? { ...c, x, y } : c))
    );
    setCircuitResult(null);
  }, []);

  const handleDeleteComponent = useCallback((instanceId: string) => {
    setComponents((prev) => prev.filter((c) => c.instanceId !== instanceId));
    setWires((prev) =>
      prev.filter((w) => w.fromComponentId !== instanceId && w.toComponentId !== instanceId)
    );
    if (selectedComponentId === instanceId) setSelectedComponentId(null);
    setCircuitResult(null);
  }, [selectedComponentId]);

  const handleAddWire = useCallback((wire: Wire) => {
    setWires((prev) => [...prev, wire]);
    setCircuitResult(null);
  }, []);

  const handleDeleteWire = useCallback((wireId: string) => {
    setWires((prev) => prev.filter((w) => w.id !== wireId));
    setCircuitResult(null);
  }, []);

  const handleToggleSwitch = useCallback((instanceId: string) => {
    setComponents((prev) =>
      prev.map((c) =>
        c.instanceId === instanceId ? { ...c, switchState: !c.switchState } : c
      )
    );
  }, []);

  const handleTestCircuit = useCallback(() => {
    setIsLoading(true);
    // Backend integration point: POST /api/circuit/validate with { components, wires }
    setTimeout(() => {
      const result = validateCircuit(components, wires);
      setCircuitResult(result);
      setIsLoading(false);
    }, 400);
  }, [components, wires]);

  const handleReset = useCallback(() => {
    setComponents([]);
    setWires([]);
    setCircuitResult(null);
    setSimulationMode(false);
    setSelectedComponentId(null);
    instanceCounter = 0;
  }, []);

  return (
    <div className="flex h-full overflow-hidden bg-slate-50">
      <Toaster position="bottom-right" richColors />

      {/* Left: Component Library */}
      <ComponentLibrarySidebar selectedColor={selectedColor} onColorChange={setSelectedColor} />

      {/* Center: Canvas + Toolbar */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <SimulatorToolbar
          onTest={handleTestCircuit}
          onReset={handleReset}
          simulationMode={simulationMode}
          onToggleSimulation={() => setSimulationMode((v) => !v)}
          wireCount={wires.length}
          componentCount={components.length}
          isLoading={isLoading}
          circuitResult={circuitResult}
        />
        <SimulatorCanvas
          components={components}
          wires={wires}
          selectedColor={selectedColor}
          simulationMode={simulationMode}
          selectedComponentId={selectedComponentId}
          onSelectComponent={setSelectedComponentId}
          onDropComponent={handleDropComponent}
          onMoveComponent={handleMoveComponent}
          onDeleteComponent={handleDeleteComponent}
          onAddWire={handleAddWire}
          onDeleteWire={handleDeleteWire}
          onToggleSwitch={handleToggleSwitch}
          circuitResult={circuitResult}
        />
      </div>

      {/* Right: Analysis Panel */}
      <CircuitAnalysisPanel
        result={circuitResult}
        components={components}
        wires={wires}
        isLoading={isLoading}
        selectedComponentId={selectedComponentId}
        onSelectComponent={setSelectedComponentId}
      />
    </div>
  );
}